import React, { useState, useRef } from "react";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import { Upload, X } from "lucide-react";

const FileUploadHandler = ({ modelId, onUploadComplete, auth, t }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const { theme } = useUserPreferences();
  const isDark = theme === "dark";

  // File selection handler with validation
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.type === "text/plain" ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".txt")
    );

    if (validFiles.length !== files.length) {
      setError(t.onlyPdfAndTxtSupported);
      event.target.value = null;
      return;
    }

    setSelectedFiles(validFiles);
    setError(null);
  };

  const getPresignedUrl = async (file) => {
    try {
      console.log(
        "Making request with token:",
        auth.user.access_token.substring(0, 20) + "..."
      );
      console.log(
        "Request URL:",
        `https://1spt18lza4.execute-api.eu-central-1.amazonaws.com/api/models/${modelId}/upload-url`
      );
      console.log("Request body:", {
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
      });

      const response = await fetch(
        `https://1spt18lza4.execute-api.eu-central-1.amazonaws.com/api/models/${modelId}/upload-url`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.user.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type || "application/octet-stream",
          }),
        }
      );

      // Add response debugging
      console.log("Response status:", response.status);
      const responseText = await response.text();
      console.log("Response text:", responseText);

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${responseText}`
        );
      }

      const data = JSON.parse(responseText);

      if (!data.url || !data.key || !data.doc_id) {
        throw new Error("Invalid response from server");
      }

      return {
        url: data.url,
        key: data.key,
        doc_id: data.doc_id,
      };
    } catch (error) {
      console.error("Detailed error:", {
        message: error.message,
        stack: error.stack,
        token: auth.user?.access_token ? "Present" : "Missing",
        modelId: modelId,
      });
      throw error;
    }
  };

  const uploadFileToS3 = async (url, file) => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Upload to S3 failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error uploading to S3:", error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          // Get presigned URL with error handling
          const { url, key, doc_id } = await getPresignedUrl(file);
          console.log("Received presigned URL:", { url, key, doc_id });

          // Upload to S3
          await uploadFileToS3(url, file);

          // Update progress
          setProgress(((i + 1) / selectedFiles.length) * 100);
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          setError(`Error uploading ${file.name}: ${error.message}`);
          break;
        }
      }

      // Reset form state on success
      if (!error) {
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        onUploadComplete();
        setError("Upload completed successfully");
      }
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((files) => files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <label
          className={`flex items-center gap-2 px-4 py-2 ${
            isDark
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-blue-500 hover:bg-blue-600"
          } text-white rounded cursor-pointer transition-colors duration-200`}
        >
          <Upload size={20} />
          <span>{t.selectFiles || "Select Files"}</span>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.txt"
            multiple
            className="hidden"
            disabled={isUploading}
          />
        </label>

        {selectedFiles.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className={`px-4 py-2 ${
              isDark
                ? "bg-green-600 hover:bg-green-700"
                : "bg-green-500 hover:bg-green-600"
            } text-white rounded transition-colors duration-200 disabled:opacity-50`}
          >
            {isUploading
              ? t.uploadingFiles || "Uploading..."
              : t.uploadFiles || "Upload Selected Files"}
          </button>
        )}
      </div>

      {selectedFiles.map((file, index) => (
        <div
          key={index}
          className={`flex items-center justify-between p-2 ${
            isDark ? "bg-gray-700" : "bg-gray-50"
          } rounded`}
        >
          <span
            className={`text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}
          >
            {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </span>
          <button
            onClick={() => removeFile(index)}
            className={`p-1 rounded transition-colors duration-200 ${
              isDark ? "hover:bg-gray-600" : "hover:bg-gray-200"
            }`}
          >
            <X
              size={16}
              className={isDark ? "text-gray-300" : "text-gray-700"}
            />
          </button>
        </div>
      ))}

      {isUploading && (
        <div className="w-full">
          <div
            className={`h-2 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`}
          >
            <div
              className={`h-2 rounded transition-all duration-300 ${
                isDark ? "bg-blue-600" : "bg-blue-500"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div
            className={`text-sm text-center mt-1 ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {Math.round(progress)}%
          </div>
        </div>
      )}

      {error && (
        <div
          className={`p-3 rounded text-sm ${
            error.includes("success")
              ? isDark
                ? "bg-green-900/50 text-green-200 border border-green-700"
                : "bg-green-100 text-green-700 border border-green-200"
              : isDark
              ? "bg-red-900/50 text-red-200 border border-red-700"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUploadHandler;
